<script lang="ts" setup>
import { object, string } from 'yup';

import type { BreadcrumbItemType } from '~/components/base/Breadcrumb.vue';
import type { SectionType } from '~/utils/api/common';

const { t } = useI18n();
const route = useValidatedRoute({ params: object().shape({ slug: string().required() }) });
const getImage = useImage();
const dataLayer = useDataLayer();
const ym = useYM();

const [{ data }, { data: commonData }] = await Promise.all([
  useAsyncData(`/products/${route.params.slug}`, () => ProductAPI.details({ params: { slug: route.params.slug } })),
  useAsyncCommonData(),
]);

assertNotEmpty(data, () => createError({ statusCode: 404, statusMessage: 'Page Not Found' }));

const selectedSku = ref(data.value.product.skus.find((item) => item.isAvailable));
const isRefillModeSelected = ref(false);

const popularProducts = computed(() =>
  commonData.value.products.filter((el) => data.value.viewedProductIds.includes(el.id) && !el.secret),
);
const similarProducts = computed(() =>
  commonData.value.products.filter((el) => data.value.boughtWithProductIds.includes(el.id) && !el.secret),
);
const supplementProducts = computed(() =>
  commonData.value.products.filter((el) => data.value.productSupplementIds.includes(el.id) && !el.secret),
);
const kitProducts = computed(() =>
  commonData.value.products.filter((el) => data.value.product.kit?.ids.includes(el.id) && !el.secret),
);
const refillProduct = computed(() =>
  data.value.product.refillId
    ? commonData.value.products.find((el) => el.id === data.value.product.refillId && el.isAvailable && !el.secret)
    : undefined,
);
const breadcrumbs = computed(() => {
  const sections: SectionType[] = [];

  const nestedSection = commonData.value.sections.find(
    (section) => section.parentId && data.value.product.sectionIds.includes(section.id),
  );

  if (nestedSection) {
    const parentSection = commonData.value.sections.find((section) => section.id === nestedSection.parentId);

    if (parentSection) sections.push(parentSection, nestedSection);
    else sections.push(nestedSection);
  } else {
    const firstSection = commonData.value.sections.find((section) => section.id === data.value.product.sectionIds[0]);

    if (firstSection) sections.push(firstSection);
  }

  return sections.reduce(
    (acc, section) => {
      const prefix = `${acc.prefix}/${section.code}`;

      return {
        prefix,
        items: [...acc.items, { label: section.name, to: prefix }],
      };
    },
    { prefix: '/catalog', items: [] as BreadcrumbItemType[] },
  ).items;
});

const enabledRelatedSection = computed(() => !!data.value.product.relatedProducts?.length);
const enabledGuideSection = computed(() => !!data.value.product.guide);
const enabledEffectSection = computed(
  () =>
    !!(
      data.value.product.action?.picturePath ||
      data.value.product.action?.text ||
      data.value.product.action?.actions.length
    ),
);
const enabledCompositionSection = computed(
  () =>
    !!(
      data.value.product.composition?.picturePath ||
      data.value.product.composition?.text ||
      data.value.product.composition?.compositions.length
    ),
);
const enabledModeOfApplicationSection = computed(
  () => !!(data.value.product.wayOfApplication?.picturePath || data.value.product.wayOfApplication?.text),
);
const enabledSizesSection = computed(() => !!(data.value.product.size?.picturePath || data.value.product.size?.text));

useSchemaOrg([
  defineProduct({
    name: data.value.product.name,
    description: data.value.product.description,
    image: data.value.product.picturePaths.map((el) => getImage(el)),
    offers: data.value.product.skus.map((sku) => ({
      price: sku.price,
      priceCurrency: PRICE_CURRENCY,
    })),
    review: data.value.review.collection.map((review) => ({
      name: review.title,
      author: review.nameUser,
      reviewRating: {
        ratingValue: review.rating,
        bestRating: 5,
        worstRating: 1,
      },
      reviewBody: review.text,
    })),
    aggregateRating: {
      ratingValue: data.value.review.rating,
      ratingCount: data.value.review.total,
      reviewCount: data.value.review.total,
      bestRating: 5,
      worstRating: 1,
    },
  }),
]);

onMounted(() => {
  const firstSku = data.value.product.skus[0];
  if (!firstSku) return;

  dataLayer.append(
    {
      event: 'detailview',
      ecommerce: {
        detail: {
          actionField: { list: '' },
          products: [
            {
              id: firstSku.id,
              xmlId: firstSku.xmlId,
              price: firstSku.price,
              name: firstSku.distinct,
              position: 1,
            },
          ],
        },
      },
    },
    ['user'],
  );
});

if (route.params.slug === 'nabor_den_i_noch_dlya_zaboty_o_kozhe_s_utra_i_pered_snom') {
  useRobotsRule({ noindex: true, nofollow: true });
}
</script>

<template>
  <main class="productPage">
    <Head>
      <Title v-if="data.product.seo.title">{{ data.product.seo.title }}</Title>
      <Meta v-if="data.product.seo.description" name="description" :content="data.product.seo.description" />
      <Meta v-if="data.product.seo.title" property="og:title" :content="data.product.seo.title" />
      <Meta v-if="data.product.seo.description" property="og:description" :content="data.product.seo.description" />
    </Head>

    <div class="productPage__content">
      <ProductInfoSection :product="data.product" :review="data.review" v-model:selected="selectedSku">
        <template #header>
          <BaseBreadcrumb :items="[{ label: t('catalog'), to: '/catalog' }, ...breadcrumbs]" />
        </template>

        <ProductRelatedSection v-if="enabledRelatedSection" :product="data.product" />
        <LazyProductSubscription
          v-if="refillProduct"
          v-model:checked="isRefillModeSelected"
          :product="refillProduct"
          class="productPage__subscription"
        />
        <LazyNeverProductGuideSection v-if="enabledGuideSection" hydrate-never :product="data.product" />
      </ProductInfoSection>
      <LazyNeverProductEffectSection v-if="enabledEffectSection" hydrate-never :product="data.product" />
      <LazyIdleProductCompositionSection v-if="enabledCompositionSection" hydrate-on-idle :product="data.product" />
      <LazyNeverProductModeOfApplicationSection
        v-if="enabledModeOfApplicationSection"
        hydrate-never
        :product="data.product"
      />
      <LazyNeverProductSizesSection v-if="enabledSizesSection" hydrate-never :product="data.product" />

      <ProductPurchaseWidget
        v-if="!data.product.previewMode"
        :variants="data.product.skus"
        :product="data.product"
        :selected="selectedSku"
        :refill="isRefillModeSelected"
      />
    </div>
    <div class="productPage__additional">
      <LazyIdleProductSimilarProducts hydrate-on-idle :products="kitProducts" :title="t('section.kit')" />
      <LazyIdleProductSimilarProducts
        @add="ym.append('reachGoal', 'add_recommendation_product')"
        hydrate-on-idle
        :products="supplementProducts"
        :title="t('section.supplement')"
      />
      <LazyIdleProductSimilarProducts hydrate-on-idle :products="popularProducts" :title="t('section.popular')" />
      <LazyIdleProductSimilarProducts hydrate-on-idle :products="similarProducts" :title="t('section.similar')" />
    </div>
    <!-- <ProductReviewsSection -->
    <!--   :product="data.product" -->
    <!--   :reviews="data.review.collection" -->
    <!-- /> -->
    <LazyIdleSectionSubscribe hydrate-on-idle />
  </main>
</template>

<i18n>
ru:
  catalog: Каталог
  section:
    kit: В набор входят
    supplement: Дополните свой уход
    popular: Популярные продукты
    similar: С этим продуктом часто покупают

en:
  catalog: Catalog
  section:
    kit: The kit includes
    supplement: Complete your care
    popular: Popular products
    similar: Frequently bought with this item
</i18n>

<style lang="scss">
.productPage {
  $self: &;

  &__content {
    position: relative;

    & > section:nth-child(2n) {
      background-color: #e8e8e8;
    }

    & .content-container {
      @include mq('none', 'sm') {
        padding-bottom: 124px;
      }
    }
  }

  &__additional {
    padding-top: 40px;
    padding-bottom: 40px;
    display: flex;
    flex-direction: column;
    gap: 20px;

    &:empty {
      display: none;
    }
  }

  &__subscription {
    margin-top: 20px;
  }

  @include mq('sm') {
    & + .footer {
      padding-bottom: 55px;
    }
  }
}
</style>
